import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { signIn, signOut, refreshSession, userPool } from "../lib/cognito";
import type { CognitoUserSession } from "amazon-cognito-identity-js";

interface AuthUser {
  email: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setIsLoading(false);
      return;
    }
    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (!err && session?.isValid()) {
          const payload = session.getIdToken().payload;
          const email = (payload["email"] ?? payload["cognito:username"] ?? "") as string;
          setUser({ email });
        }
        setIsLoading(false);
      }
    );
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const cognitoUser = await signIn(username, password);
    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (!err && session?.isValid()) {
          const payload = session.getIdToken().payload;
          const display = (payload["email"] ?? payload["cognito:username"] ?? username) as string;
          setUser({ email: display });
        }
      }
    );
  }, []);

  const logout = useCallback(() => {
    signOut();
    setUser(null);
    navigate("/");
  }, [navigate]);

  const getToken = useCallback(async () => {
    return refreshSession();
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
